/**
 * Routes where authenticated user-context queries must NOT auto-fetch.
 *
 * These are pre-auth launch handoffs (the EHR launch pages render under
 * MinimalProviders and navigate to the session once the fresh launch token is
 * applied) and terminal pages where `/user` 403s. Gating user-context queries
 * here prevents them from firing with a stale rehydrated token during the
 * launch bootstrap window (which would 401 and trigger a forced logout).
 *
 * Lives in its own dependency-free module so both `useQueryUser` and
 * `useQueryKindeProperties` can consume the predicate without a circular import
 * (`useQueryUser` imports `useQueryKindeProperties`).
 */
const USER_QUERY_BLOCKED_PATHS = new Set([
  // Hosted onboarding page, rendered mid-login under MinimalProviders with no
  // token; keeps the tree's authed syncs from firing with a stale one.
  '/auth/onboard',
  '/auth/onboard-complete',
  '/auth/onboard-expired',
  '/organization-access-required',
  // Terminal page for suspended accounts — /user 403s here, so block it.
  '/account-suspended',
  // Cannot query user data on Haiku pre-auth launch routes
  '/integration/epic/haiku/[...id]',
  '/integration/epic/haiku/[region]/[profile_name]/[external_session_id]',
  // SMART (nCAS/Cerner/...) launch handoff renders under MinimalProviders and
  // navigates to the session once the fresh launch token is applied. Blocking
  // here stops the minimal tree's authed syncs (useQueryUser,
  // SafeTeamMembershipSync, SafeOpenFeatureContextSync) and the nested
  // useQueryKindeProperties from firing with the stale rehydrated token during
  // the bootstrap window.
  '/integration/smart-on-fhir/launch',
  // Patient pre-consult page: no clinician is signed in, and the page must make no clinician calls.
  '/patient-sessions/pre-consult/[token]',
]);

export const isUserQueryBlockedRoute = (pathname: string) =>
  USER_QUERY_BLOCKED_PATHS.has(pathname);
